// ============================================================================
// TRIAGE QUEUE VIEW — Triage Nurse primary workspace
//
// Role: triage_nurse only (RouteGuard enforces before this renders).
//
// Tabs:
//   Pending Triage — READY_TO_SCHEDULE, not on hold, not completed
//                    sorted: Urgent group → Routine group → Overridden group
//   On Hold        — onHold === true (any workflow status)
//   Triaged        — triageCompletedAt exists
//
// Triage status is DERIVED from workflow/state fields, not a new persistent
// status category. The canonical workflow state machine is unchanged.
//
// Triage Drawer (inline slide-over):
//   Referral Receipt panel (read-only, non-PHI) — 4 sections:
//     1. Referral Header   — id, specialty, pack, submitted date
//     2. Decision Map      — Gate outcome, override status, workflow status, priority
//     3. Rework Summary    — rework request count, missing items count
//     4. Workflow Context  — submitted by, assigned to, triage completion info
//   Triage Form (bounded fields only):
//     priority, onHold, onHoldReason, overrideDecision, overrideReason,
//     overrideNote, triageNote
//   Auto priority default:
//     Gate URGENT → triage URGENT   (if priority not already set)
//     Gate ROUTINE / override → NORMAL
//
// Zero-PHI invariant: no patient name, DOB, or PHI rendered.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  ClipboardList, Loader2, AlertCircle, RefreshCw,
  ChevronRight, X, Save, CheckCircle2,
  PauseCircle, Clock, Filter, FileText,
  ChevronDown, ChevronUp, Eye, Paperclip, RotateCcw,
} from 'lucide-react';
import { listAttachments } from '../core/phiAttachmentsApi.js';
import { getApp } from 'firebase/app';
import { navigateToAttachmentViewer } from '../components/AttachmentPdfViewer.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';
import { getPriorityMeta, getReworkStatusMeta } from '../core/statusConfig.js';
import {
  getFirestore, doc, getDoc, collection, getDocs,
} from 'firebase/firestore';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY, TRIAGE_DECISION,
  listOpsActiveItems,
  COMMUNICATION_PURPOSE,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import { canCreateReferralPing } from '../core/rbac.js';
import { saveTriage, returnToIntakeForRework } from '../core/store.triage.js';
import { REWORK_ITEM_TYPE, REWORK_ITEM_TYPE_LABELS } from '../core/store.rework.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PATHS from '../core/firestorePaths.js';
import PingComposer from './PingComposer.jsx';

// ── DB singleton ──────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── Display configs ───────────────────────────────────────────────────────────
// Status/outcome/priority badge visuals are centralized in src/core/statusConfig.js
// and rendered via <RGStatusBadge>. No local status color maps live here.

// Token-backed chip for derived/non-enum labels.
const CHIP_CLS = 'inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium';

// Derived triage display status (NOT a workflow enum) — token-backed.
const TRIAGE_DISPLAY = {
  PENDING:  { label: 'Pending Triage', token: 'neutral' },
  ON_HOLD:  { label: 'On Hold',        token: 'on-hold' },
  TRIAGED:  { label: 'Triaged',        token: 'success' },
};

function TriageStatusChip({ statusKey }) {
  const t = TRIAGE_DISPLAY[statusKey] ?? TRIAGE_DISPLAY.PENDING;
  return (
    <span className={`${CHIP_CLS} bg-status-${t.token} text-status-${t.token} border-status-${t.token}`}>
      {t.label}
    </span>
  );
}

// Override decision options
const OVERRIDE_OPTIONS = [
  { value: '',                            label: '— None —' },
  { value: TRIAGE_DECISION.OK,            label: 'OK — Proceed as normal' },
  { value: TRIAGE_DECISION.URGENT_REVIEW, label: 'Urgent Review — escalate priority' },
  { value: TRIAGE_DECISION.REDIRECT,      label: 'Redirect — wrong specialty' },
];

// Pending group display config
const PENDING_GROUPS = [
  { key: 'urgent',    label: 'Urgent',     labelTw: 'text-red-400',   dividerTw: 'border-red-500/20' },
  { key: 'routine',   label: 'Routine',    labelTw: 'text-slate-400', dividerTw: 'border-slate-700/30' },
  { key: 'overridden',label: 'Overridden', labelTw: 'text-amber-400', dividerTw: 'border-amber-500/20' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Get referral outcome key from referral doc. */
function getReferralOutcome(referral) {
  if (referral?.finalOutcome) return referral.finalOutcome;
  if (referral?.outcome?.status) return referral.outcome.status;
  return null;
}

/** True if the outcome is URGENT (handles both cases). */
function isUrgentOutcome(outcome) {
  return outcome === 'URGENT' || outcome === 'urgent';
}

/**
 * Canonical override check.
 * Primary: referral doc (Gate-authored).
 * Fallback: workflow/state (triage-authored).
 */
function isOverridden(referral, workflow) {
  if (referral?.outcome?.isOverride === true) return true;
  if (referral?.override?.used === true)      return true;
  return !!(
    workflow?.overrideDecision ||
    workflow?.overrideReason   ||
    workflow?.overriddenBy     ||
    workflow?.overriddenAt
  );
}

/** Extract override detail fields, normalising Gate + triage sources. */
function getOverrideDetail(referral, workflow) {
  const gateReason   = referral?.outcome?.overrideReason ?? referral?.override?.reason           ?? null;
  const gateNote     = referral?.override?.note                                                   ?? null;
  const gateBy       = referral?.outcome?.overrideByUid  ?? referral?.override?.confirmedByUid   ?? null;
  const gateAt       = referral?.outcome?.overrideAt     ?? referral?.override?.confirmedAt       ?? null;
  const triageReason = workflow?.overrideReason ?? null;
  const triageNote   = workflow?.overrideNote   ?? null;
  const triageBy     = workflow?.overriddenBy   ?? null;
  const triageAt     = workflow?.overriddenAt   ?? null;
  return {
    reason: gateReason ?? triageReason,
    note:   gateNote   ?? triageNote,
    by:     gateBy     ?? triageBy,
    at:     gateAt     ?? triageAt,
  };
}

/** Derive triage display status from workflow/state fields. */
function deriveTriageStatus(workflow) {
  if (!workflow)                return 'PENDING';
  if (workflow.onHold === true) return 'ON_HOLD';
  if (workflow.triage?.triageCompletedAt || workflow.triageCompletedAt) return 'TRIAGED';
  return 'PENDING';
}

/**
 * Auto-derive default priority for the triage form.
 * - If workflow already has a priority set → use it.
 * - Gate URGENT → URGENT (unless overridden).
 * - Everything else → NORMAL.
 */
function getDefaultPriority(workflow, referral) {
  if (workflow?.priority) return workflow.priority;
  if (isOverridden(referral, workflow)) return WORKFLOW_PRIORITY.NORMAL;
  const outcome = getReferralOutcome(referral);
  if (isUrgentOutcome(outcome)) return WORKFLOW_PRIORITY.URGENT;
  return WORKFLOW_PRIORITY.NORMAL;
}

/** Format a Firestore Timestamp or ISO string for display. */
function formatTs(ts) {
  if (!ts) return '—';
  try {
    const ms = ts?.toMillis
      ? ts.toMillis()
      : ts?.seconds
        ? ts.seconds * 1000
        : new Date(ts).getTime();
    return new Date(ms).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/**
 * Sort pending items into three groups: urgent → routine → overridden.
 * Oldest first within each group (most-stuck at top — work the longest-waiting first).
 */
function groupPendingItems(items) {
  const sortByOldest = (a, b) => {
    const tsA = a.workflow?.statusEnteredAt?.seconds ?? a.referral?.createdAt?.seconds ?? 0;
    const tsB = b.workflow?.statusEnteredAt?.seconds ?? b.referral?.createdAt?.seconds ?? 0;
    return tsA - tsB;
  };

  const urgent    = [];
  const routine   = [];
  const overridden = [];

  for (const item of items) {
    const { referral, workflow } = item;
    if (isOverridden(referral, workflow)) {
      overridden.push(item);
    } else {
      const outcome = getReferralOutcome(referral);
      if (isUrgentOutcome(outcome) || workflow?.priority === WORKFLOW_PRIORITY.URGENT) {
        urgent.push(item);
      } else {
        routine.push(item);
      }
    }
  }

  return {
    urgent:     urgent.sort(sortByOldest),
    routine:    routine.sort(sortByOldest),
    overridden: overridden.sort(sortByOldest),
  };
}

// ── Small components ──────────────────────────────────────────────────────────

/** Resolve a UID to a display name via users/{uid}. */
function emailToName(email) {
  if (!email) return null;
  const local = email.split('@')[0] ?? '';
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || null;
}

function MemberName({ uid }) {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    if (!uid) return;
    getDoc(doc(getDb(), `users/${uid}`))
      .then((snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setLabel(d.displayName || d.name || emailToName(d.email) || null);
      })
      .catch(() => {});
  }, [uid]);
  if (!uid) return <span className="text-slate-500">—</span>;
  return (
    <span className="text-slate-300 text-xs">
      {label ?? `…${String(uid).slice(-8)}`}
    </span>
  );
}

// ── Tab definitions ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'PENDING', label: 'Pending Triage', icon: Clock },
  { id: 'ON_HOLD', label: 'On Hold',        icon: PauseCircle },
  { id: 'TRIAGED', label: 'Triaged',        icon: CheckCircle2 },
];

// ── Referral Receipt helpers ──────────────────────────────────────────────────

/**
 * Convert a camelCase or snake_case field ID to a readable label.
 * e.g. "primaryAbnormalCbcReferralReason" → "Primary Abnormal Cbc Referral Reason"
 */
function formatFieldLabel(fieldId) {
  return String(fieldId)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Format an answer value for display.
 * Arrays → comma-joined string. Booleans → Yes/No. Else → string cast.
 */
function formatAnswerValue(val) {
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  const s = String(val).trim();
  return s || '—';
}

// ── Referral Receipt helpers ──────────────────────────────────────────────────

const REWORK_TYPE_LABELS = {
  ATTACHMENT:      'Attachment',
  LAB_RESULT:      'Lab Result',
  CLINICAL_NOTE:   'Clinical Note',
  IMAGING:         'Imaging',
  PRESCRIPTION:    'Prescription',
  REFERRAL_LETTER: 'Referral Letter',
};

// ── Referral Receipt (read-only, non-PHI) ────────────────────────────────────

/**
 * Read-only collapsible receipt panel shown inside the triage drawer.
 *
 * Fetches (non-PHI paths only):
 *   1. tenants/{tenantId}/referrals/{referralId}
 *        full doc → answers, missingFieldIds, patient.*, outcome.*,
 *        specialtyId, packId, versionId, createdAt, createdByUid
 *   2. tenants/{tenantId}/referrals/{referralId}/auditLogs/{referralId}
 *        → answersSnapshot, outcome.completeness, outcome.missingRequired[],
 *          outcome.triggeredRedFlags[]
 *   3. tenants/{tenantId}/referrals/{referralId}/reworkRequests (full docs)
 *        → itemType, status, createdAt (non-PHI)
 *
 * Sections:
 *   1. Referral Header            — id, specialty, pack, version, submitted
 *   2. Patient Context (non-PHI)  — visit type, reason for escalation
 *   3. Referral Summary           — outcome, completeness
 *   4. Red Flags (URGENT only)    — triggered red flags from auditLog
 *   5. Decision Map               — Gate outcome, override, status, priority
 *   6. Criteria & Completeness    — answered fields + missing fields
 *   7a. Attachments               — PHI attachments via Cloud Functions (view button)
 *   7b. Rework Requests           — rework request items (non-PHI manifest)
 *   8. Workflow Context            — submitted by, assigned to, triaged by/at
 *
 * PHI attachment access: triage_nurse can READ+DOWNLOAD via signed URLs.
 * File names are shown from the manifest; binary content is never in Firestore.
 */
function ReferralReceipt({ tenantId, referralId, referral: referralProp, workflow, activeFocusTarget }) {
  const [open,         setOpen]         = useState(true);
  const [fullReferral, setFullReferral] = useState(null);
  const [auditLog,     setAuditLog]     = useState(undefined); // undefined=loading, null=not found
  const [reworkItems,  setReworkItems]  = useState(null);      // full rework request docs

  // ── Attachment state (PHI plane — fetched via Cloud Functions) ──────────
  const [attachments,    setAttachments]    = useState(null);   // null=not loaded, []=empty
  const [attLoading,     setAttLoading]     = useState(false);
  const [attError,       setAttError]       = useState(null);
  // ── 1. Fetch full referral doc ──────────────────────────────────────────
  // Path: tenants/{tenantId}/referrals/{referralId}
  // Needed for: answers (Firestore field name), missingFieldIds, patient.*
  useEffect(() => {
    if (!tenantId || !referralId) return;
    getDoc(doc(getDb(), PATHS.referral(tenantId, referralId)))
      .then((snap) => setFullReferral(snap.exists() ? snap.data() : null))
      .catch(() => setFullReferral(null));
  }, [tenantId, referralId]);

  // ── 2. Fetch auditLog (runId = referralId, Model A) ─────────────────────
  // Path: tenants/{tenantId}/referrals/{referralId}/auditLogs/{referralId}
  useEffect(() => {
    if (!tenantId || !referralId) return;
    getDoc(doc(getDb(), PATHS.referralAuditLog(tenantId, referralId, referralId)))
      .then((snap) => setAuditLog(snap.exists() ? snap.data() : null))
      .catch(() => setAuditLog(null));
  }, [tenantId, referralId]);

  // ── 3. Fetch rework request docs (non-PHI) ──────────────────────────────
  // Path: tenants/{tenantId}/referrals/{referralId}/reworkRequests
  useEffect(() => {
    if (!tenantId || !referralId) return;
    getDocs(collection(getDb(), PATHS.col.reworkRequests(tenantId, referralId)))
      .then((snap) => setReworkItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setReworkItems([]));
  }, [tenantId, referralId]);

  // ── 4. Fetch PHI attachments via Cloud Function ────────────────────────
  useEffect(() => {
    if (!referralId) return;
    setAttLoading(true);
    setAttError(null);
    listAttachments(referralId)
      .then((res) => setAttachments(Array.isArray(res?.attachments) ? res.attachments : []))
      .catch(() => {
        setAttachments([]);
        setAttError('Unable to load attachments.');
      })
      .finally(() => setAttLoading(false));
  }, [referralId]);


  // ── Derived values ──────────────────────────────────────────────────────

  // Use full referral doc when available; fall back to prop for identity fields
  const referral = fullReferral ?? referralProp;
  const outcome  = getReferralOutcome(referral);
  const isUrgent = isUrgentOutcome(outcome);

  // Completeness %
  const completeness = auditLog?.outcome?.completeness ?? auditLog?.completeness ?? null;

  // Missing fields — auditLog first, then referral.missingFieldIds (actual Firestore field)
  const missingFields = auditLog?.outcome?.missingRequired
    ?? auditLog?.outcome?.missing
    ?? auditLog?.missingRequired
    ?? fullReferral?.missingFieldIds
    ?? [];

  // Triggered red flags (URGENT only) — try all known field names
  const triggeredRedFlags = auditLog?.outcome?.triggeredRedFlags
    ?? auditLog?.outcome?.redFlagMatches
    ?? auditLog?.outcome?.triggeredFlagIds
    ?? auditLog?.outcome?.triggeredFlags
    ?? fullReferral?.outcome?.triggeredRedFlags
    ?? fullReferral?.outcome?.redFlagMatches
    ?? [];

  // Clinical answers — referral.answers is the actual Firestore field name (not clinicalAnswers)
  const answers = auditLog?.answersSnapshot
    ?? auditLog?.clinicalAnswers
    ?? auditLog?.answers
    ?? fullReferral?.clinicalAnswers
    ?? fullReferral?.answers   // ← actual field name in Firestore
    ?? null;

  // Build criteria rows
  const criteriaRows = (() => {
    if (!answers && missingFields.length === 0) return [];
    const rows = [];
    if (answers && typeof answers === 'object') {
      for (const [fieldId, val] of Object.entries(answers)) {
        rows.push({ fieldId, val, answered: !missingFields.includes(fieldId) });
      }
    }
    for (const fieldId of missingFields) {
      if (!answers || !(fieldId in answers)) {
        rows.push({ fieldId, val: null, answered: false });
      }
    }
    return rows;
  })();

  const isLoading = fullReferral === null && auditLog === undefined;

  return (
    <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg overflow-hidden">
      {/* Collapse toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wide hover:text-slate-300 transition-colors"
      >
        <span className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          Referral Receipt
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="border-t border-slate-700/30 divide-y divide-slate-700/20">

          {/* ── Section 1: Referral Header ──────────────────────────────── */}
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Referral Header
            </p>
            <div className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-xs">
              <span className="text-slate-500">ID</span>
              <span className="text-slate-300 font-mono break-all text-[10px]">{referralId || '—'}</span>
              <span className="text-slate-500">Specialty</span>
              <span className="text-slate-300">{referral?.specialtyId?.replace(/_/g, ' ') ?? '—'}</span>
              <span className="text-slate-500">Pack</span>
              <span className="text-slate-300">{referral?.packId?.replace(/_/g, ' ') ?? '—'}</span>
              {referral?.versionId && (
                <>
                  <span className="text-slate-500">Version</span>
                  <span className="text-slate-400 font-mono text-[10px]">{referral.versionId}</span>
                </>
              )}
              <span className="text-slate-500">Submitted</span>
              <span className="text-slate-300">{formatTs(referral?.createdAt)}</span>
            </div>
          </div>

          {/* ── Section 2: Patient Context (non-PHI) ────────────────────── */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Patient Context
              </p>
              <span className="text-[10px] text-slate-600 italic">Non-PHI only</span>
            </div>
            {isLoading ? (
              <p className="text-xs text-slate-600 italic">Loading…</p>
            ) : (
              <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-xs">
                {/* visitType — check nested patient.* first, then top-level */}
                {(referral?.patient?.visitType ?? referral?.visitType) ? (
                  <>
                    <span className="text-slate-500">Visit Type</span>
                    <span className="text-slate-300 capitalize">
                      {String(referral?.patient?.visitType ?? referral?.visitType).replace(/-/g, ' ')}
                    </span>
                  </>
                ) : null}
                {/* reasonForEscalation — check nested patient.* first, then top-level */}
                {(referral?.patient?.reasonForEscalation ?? referral?.reasonForEscalation) ? (
                  <>
                    <span className="text-slate-500">Reason</span>
                    <span className="text-slate-300">
                      {formatAnswerValue(
                        referral?.patient?.reasonForEscalation ?? referral?.reasonForEscalation
                      )}
                    </span>
                  </>
                ) : null}
                {!referral?.patient?.visitType && !referral?.visitType &&
                 !referral?.patient?.reasonForEscalation && !referral?.reasonForEscalation && (
                  <span className="text-slate-600 italic col-span-2 text-[11px]">
                    No non-PHI patient context recorded.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ── Section 3: Referral Summary ─────────────────────────────── */}
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Referral Summary
            </p>
            <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-xs">
              <span className="text-slate-500">Specialty</span>
              <span className="text-slate-300 capitalize">{referral?.specialtyId?.replace(/_/g, ' ') ?? '—'}</span>
              <span className="text-slate-500">Pack</span>
              <span className="text-slate-300 capitalize">{referral?.packId?.replace(/_/g, ' ') ?? '—'}</span>
              <span className="text-slate-500">Outcome</span>
              <span className="flex items-center gap-1.5">
                {outcome
                  ? <RGStatusBadge type="outcome" status={outcome} />
                  : <span className="text-slate-500">—</span>}
                {completeness !== null && (
                  <span className="text-slate-500 text-[10px]">({completeness}% complete)</span>
                )}
              </span>
            </div>
          </div>

          {/* ── Section 4: Red Flags (URGENT only) ──────────────────────── */}
          {isUrgent && (
            <div className="px-4 py-3 bg-red-500/5">
              <p className="text-xs font-medium text-red-400 uppercase tracking-wide mb-2">
                Red Flags — Urgent Triggers
              </p>
              {auditLog === undefined ? (
                <p className="text-xs text-slate-600 italic">Loading…</p>
              ) : triggeredRedFlags.length > 0 ? (
                <div className="space-y-1">
                  {triggeredRedFlags.map((flag, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-red-400 text-xs flex-shrink-0 mt-0.5">⚠</span>
                      <span className="text-xs text-red-200">
                        {formatFieldLabel(String(flag))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                // auditLog exists but no explicit flag list — show the outcome reason if present
                <p className="text-xs text-red-300/70">
                  {fullReferral?.outcome?.reason
                    ?? auditLog?.outcome?.reason
                    ?? 'Urgent red flag triggered — details in audit log.'}
                </p>
              )}
            </div>
          )}

          {/* ── Section 5: Decision Map ──────────────────────────────────── */}
          <div className={`px-4 py-3${activeFocusTarget === 'triage-drawer-decision-map' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-decision-map">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Decision Map
            </p>
            <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-xs items-center">
              <span className="text-slate-500">Gate Decision</span>
              <span>
                {outcome
                  ? <RGStatusBadge type="outcome" status={outcome} />
                  : <span className="text-slate-500">—</span>}
              </span>
              <span className="text-slate-500">Gate Override</span>
              <span>
                {isOverridden(referral, workflow)
                  ? <span className={`${CHIP_CLS} bg-status-warning text-status-warning border-status-warning`}>Override Applied</span>
                  : <span className="text-slate-500">None</span>}
              </span>
              <span className="text-slate-500">Referral Status</span>
              <span>
                {workflow?.status
                  ? <RGStatusBadge type="workflow" status={workflow.status} />
                  : <span className="text-slate-500">—</span>}
              </span>
              <span className="text-slate-500">Triage Status</span>
              <span>
                <TriageStatusChip statusKey={deriveTriageStatus(workflow)} />
              </span>
              <span className="text-slate-500">Priority</span>
              <span>
                {workflow?.priority
                  ? <RGStatusBadge type="priority" status={workflow.priority} />
                  : <span className="text-slate-500">—</span>}
              </span>
            </div>
          </div>

          {/* ── Section 6: Criteria & Completeness ──────────────────────── */}
          <div className={`px-4 py-3${activeFocusTarget === 'triage-drawer-criteria' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-criteria">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Criteria & Completeness
              </p>
              {completeness !== null && (
                <span className={`text-xs font-semibold ${
                  completeness === 100 ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {completeness}%
                </span>
              )}
            </div>
            {auditLog === undefined ? (
              <p className="text-xs text-slate-600 italic">Loading…</p>
            ) : criteriaRows.length === 0 ? (
              <p className="text-xs text-slate-600 italic">No criteria recorded.</p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-[10px] text-slate-600 mb-2">
                  Clinical criteria for{' '}
                  <span className="text-slate-500 font-medium">
                    {(auditLog?.packId ?? referral?.packId)?.replace(/_/g, ' ')}
                  </span>
                </p>
                {criteriaRows.map(({ fieldId, val, answered }) => (
                  <div key={fieldId} className="flex items-start gap-2">
                    <span className={`mt-0.5 text-xs flex-shrink-0 font-bold ${
                      answered ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {answered ? '✓' : '✗'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-300 leading-tight">
                        {formatFieldLabel(fieldId)}
                      </p>
                      {answered && val !== null && val !== undefined && (
                        <p className="text-[11px] text-slate-500 mt-0.5 break-words">
                          {formatAnswerValue(val)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 7a: Attachments (PHI plane — via Cloud Functions) ─ */}
          <div className={`px-4 py-3${activeFocusTarget === 'triage-drawer-attachments' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-attachments">
            <div className="flex items-center gap-2 mb-2">
              <Paperclip className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Attachments
              </p>
              {attachments && attachments.length > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px] font-semibold">
                  {attachments.length}
                </span>
              )}
            </div>
            {attLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading attachments…
              </div>
            ) : attError ? (
              <p className="text-xs text-red-400">{attError}</p>
            ) : !attachments || attachments.length === 0 ? (
              <p className="text-xs text-slate-600 italic">No attachments found.</p>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((att) => (
                  <div
                    key={att.attachmentId ?? att.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg
                               bg-slate-800/40 border border-slate-700/30"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      {(att.type || att.artifactType) && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-[10px] font-semibold
                                         text-slate-300 uppercase tracking-wide flex-shrink-0">
                          {att.artifactType === 'REFERRAL_PACKET_PDF' ? 'Referral PDF'
                            : (att.type ?? '').replace(/_/g, ' ')}
                        </span>
                      )}
                      <span className="text-xs text-slate-300 truncate">
                        {att.originalName ?? att.fileName ?? `Attachment ${att.attachmentId ?? att.id}`}
                      </span>
                      {att.mimeType && (
                        <span className="text-[10px] text-slate-600 flex-shrink-0">
                          {att.mimeType.split('/').pop()?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await navigateToAttachmentViewer({ referralId, attachmentId: att.attachmentId ?? att.id, returnView: 'triage-queue' });
                        } catch {
                          // Session creation failed — user can retry
                        }
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium
                                 bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25
                                 transition-colors flex-shrink-0"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 7b: Rework Requests ────────────────────────────────── */}
          <div className={`px-4 py-3${activeFocusTarget === 'triage-drawer-rework' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-rework">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Rework Requests
            </p>
            {reworkItems === null ? (
              <p className="text-xs text-slate-600 italic">Loading…</p>
            ) : reworkItems.length === 0 ? (
              <p className="text-xs text-slate-600 italic">No rework requests.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-600 text-[10px] uppercase tracking-wide">
                    <th className="text-left pb-1.5 font-medium w-6">#</th>
                    <th className="text-left pb-1.5 font-medium">Type</th>
                    <th className="text-left pb-1.5 font-medium">Status</th>
                    <th className="text-left pb-1.5 font-medium">Requested</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/20">
                  {reworkItems.map((item, i) => {
                    const statusMeta = item.status ? getReworkStatusMeta(item.status) : null;
                    const typeLabel = REWORK_TYPE_LABELS[item.itemType] ?? item.itemType ?? '—';
                    return (
                      <tr key={item.id}>
                        <td className="py-1 text-slate-600">{i + 1}</td>
                        <td className="py-1 text-slate-300">{typeLabel}</td>
                        <td className="py-1">
                          {statusMeta
                            ? <span className={`font-medium ${statusMeta.textClassName}`}>{statusMeta.label}</span>
                            : <span className="text-slate-500">{item.status ?? '—'}</span>}
                        </td>
                        <td className="py-1 text-slate-500">{formatTs(item.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Section 8: Workflow Context ──────────────────────────────── */}
          <div className={`px-4 py-3${activeFocusTarget === 'triage-drawer-workflow' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-workflow">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Workflow Context
            </p>
            <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-xs items-baseline">
              <span className="text-slate-500">Submitted By</span>
              <MemberName uid={referral?.createdByUid} />
              <span className="text-slate-500">Assigned To</span>
              {(workflow?.ownerUid ?? workflow?.assignedTo)
                ? <MemberName uid={workflow.ownerUid ?? workflow.assignedTo} />
                : <span className="text-slate-500 italic">Unassigned</span>
              }
              {(workflow?.triage?.triageCompletedAt || workflow?.triageCompletedAt) && (
                <>
                  <span className="text-slate-500">Triaged By</span>
                  <MemberName uid={workflow?.triage?.triageCompletedBy || workflow?.triageCompletedBy} />
                  <span className="text-slate-500">Triaged At</span>
                  <span className="text-slate-300">{formatTs(workflow?.triage?.triageCompletedAt || workflow?.triageCompletedAt)}</span>
                </>
              )}
            </div>
          </div>

        </div>
      )}

    </div>
  );
}

// ── Triage Drawer ─────────────────────────────────────────────────────────────

function TriageDrawer({ item, onClose, onSaved, uid, role, activeFocusTarget }) {
  const { referral, workflow } = item;
  const outcome      = getReferralOutcome(referral);
  const triageStatus = deriveTriageStatus(workflow);

  const tenantId   = referral?.tenantId;
  const referralId = referral?.id || referral?.referralId;
  const isCompleted = triageStatus === 'TRIAGED';

  // Triage form state — pre-populate from existing workflow fields.
  // Priority auto-defaults from Gate outcome if not already set.
  const [priority,         setPriority]         = useState(() => getDefaultPriority(workflow, referral));
  const [onHold,           setOnHold]           = useState(workflow?.onHold           ?? false);
  const [onHoldReason,     setOnHoldReason]     = useState(workflow?.onHoldReason     ?? '');
  const [overrideDecision, setOverrideDecision] = useState(workflow?.overrideDecision ?? '');
  const [overrideReason,   setOverrideReason]   = useState(workflow?.overrideReason   ?? '');
  const [overrideNote,     setOverrideNote]     = useState(workflow?.overrideNote     ?? '');
  const [triageNote,       setTriageNote]       = useState(workflow?.triageNote       ?? '');

  const [saving,     setSaving]     = useState(false);
  const [completing, setCompleting] = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  // Return to Intake modal state
  const [showReturnModal,   setShowReturnModal]   = useState(false);
  const [returnReason,      setReturnReason]      = useState('');
  const [returnItemTypes,   setReturnItemTypes]   = useState([]);
  const [returning,         setReturning]         = useState(false);
  const [returnError,       setReturnError]       = useState(null);
  const canReturn = workflow?.status === WORKFLOW_STATUS.READY_TO_SCHEDULE;

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: activeFocusTarget === 'triage-drawer-receipt' ? 'start' : 'center' });
    }, 120);
    return () => clearTimeout(timer);
  }, [activeFocusTarget]);

  const buildFields = () => ({
    priority,
    onHold,
    onHoldReason:     onHold ? onHoldReason : '',
    overrideDecision: overrideDecision || null,
    overrideReason:   overrideDecision ? overrideReason : '',
    overrideNote:     overrideDecision ? overrideNote   : '',
    triageNote,
  });

  const handleSave = async (markComplete = false) => {
    if (overrideDecision && !overrideReason.trim()) {
      setSaveError('Override reason is required when an override decision is set.');
      return;
    }
    if (overrideDecision && !overrideNote.trim()) {
      setSaveError('Override note is required when an override decision is set.');
      return;
    }
    if (onHold && !onHoldReason.trim()) {
      setSaveError('On-hold reason is required when placing a referral on hold.');
      return;
    }

    setSaveError(null);
    if (markComplete) setCompleting(true); else setSaving(true);

    try {
      await saveTriage(tenantId, referralId, buildFields(), uid, markComplete);
      onSaved(referralId, buildFields(), markComplete);
      onClose();
    } catch (err) {
      console.error('[TriageDrawer] saveTriage failed:', err);
      setSaveError(safeUserError(err) ?? 'Failed to save triage. Please retry.');
    } finally {
      setSaving(false);
      setCompleting(false);
    }
  };

  const toggleReturnItemType = (type) => {
    setReturnItemTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleReturnToIntake = async () => {
    if (!returnReason.trim()) {
      setReturnError('A reason is required.');
      return;
    }
    if (returnItemTypes.length === 0) {
      setReturnError('Select at least one missing/invalid item type.');
      return;
    }
    setReturnError(null);
    setReturning(true);
    try {
      await returnToIntakeForRework(tenantId, referralId, returnReason, returnItemTypes, uid);
      onSaved(referralId, { _returned: true }, false);
      onClose();
    } catch (err) {
      console.error('[TriageDrawer] returnToIntake failed:', err);
      setReturnError(safeUserError(err) ?? 'Failed to return referral. Please retry.');
    } finally {
      setReturning(false);
    }
  };

  return (
    <>
      {/* Return to Intake confirmation modal */}
      {showReturnModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setShowReturnModal(false)} aria-hidden="true" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl w-full max-w-md">
              <div className="px-6 pt-5 pb-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <RotateCcw className="w-5 h-5 text-amber-400" />
                  Return to Intake
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  This will move the referral back to <span className="text-amber-300 font-medium">Needs Info</span> and create rework requests for intake to resolve.
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Reason */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                    Reason <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    rows={3}
                    placeholder="Why is this referral being returned to intake?"
                    className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 resize-none"
                    autoFocus
                  />
                </div>

                {/* Item type multi-select */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                    Missing / Invalid Items <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(REWORK_ITEM_TYPE).map(([key, val]) => {
                      const selected = returnItemTypes.includes(val);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleReturnItemType(val)}
                          className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                            selected
                              ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                              : 'bg-slate-800/40 border-slate-700/40 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                          }`}
                        >
                          {REWORK_ITEM_TYPE_LABELS[key]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Error */}
                {returnError && (
                  <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-300">{returnError}</p>
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="px-6 pb-5 pt-2 flex gap-3">
                <button
                  onClick={() => setShowReturnModal(false)}
                  disabled={returning}
                  className="flex-1 py-2.5 rounded-lg border border-slate-700/50 text-slate-400 hover:text-white text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReturnToIntake}
                  disabled={returning}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                >
                  {returning ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Returning…</>
                  ) : (
                    <><RotateCcw className="w-4 h-4" /> Confirm Return</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-slate-900 border-l border-slate-800 z-50 flex flex-col overflow-hidden">

        {/* Header */}
        <div className={`flex items-start justify-between px-6 py-4 border-b border-slate-800/50 flex-shrink-0${activeFocusTarget === 'triage-drawer-detail' ? ' rounded-lg transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-detail">
          <div>
            <p className="text-xs text-slate-500 font-mono mb-0.5">{referralId}</p>
            <h2 className="text-lg font-semibold text-white">
              {referral?.specialtyId?.replace(/_/g, ' ') ?? 'Referral'}
            </h2>
            <p className="text-sm text-slate-400">
              {referral?.packId?.replace(/_/g, ' ') ?? ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>


        {/* Completed notice */}
        {isCompleted && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-lg px-4 py-3 flex-shrink-0">
            <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-teal-300 font-medium">Triage completed</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Completed {formatTs(workflow?.triage?.triageCompletedAt || workflow?.triageCompletedAt)} — fields remain editable for corrections.
              </p>
            </div>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

          {/* ── Referral Receipt (read-only) ──────────────────────────────── */}
          <div className={activeFocusTarget === 'triage-drawer-receipt' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : undefined} data-learning-target="triage-drawer-receipt">
            <ReferralReceipt
              tenantId={tenantId}
              referralId={referralId}
              referral={referral}
              workflow={workflow}
              activeFocusTarget={activeFocusTarget}
            />
          </div>

          {/* ── Triage Actions ────────────────────────────────────────────── */}
          <div className={activeFocusTarget === 'triage-drawer-actions' ? 'space-y-5 rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : 'space-y-5'} data-learning-target="triage-drawer-actions">

          {/* Priority */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
              Priority
            </label>
            <div className="flex gap-2">
              {Object.entries(WORKFLOW_PRIORITY).map(([key, val]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPriority(val)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    priority === val
                      ? val === WORKFLOW_PRIORITY.URGENT
                        ? 'bg-red-500/20 border-red-500/40 text-red-300'
                        : val === WORKFLOW_PRIORITY.HIGH
                          ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                          : 'bg-slate-600/40 border-slate-500/40 text-slate-200'
                      : 'bg-slate-800/40 border-slate-700/40 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                  }`}
                >
                  {getPriorityMeta(val).label}
                </button>
              ))}
            </div>
          </div>

          {/* On Hold */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                onClick={() => setOnHold(!onHold)}
                className={`w-10 h-5 rounded-full relative transition-colors ${
                  onHold ? 'bg-orange-500' : 'bg-slate-700'
                }`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  onHold ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </div>
              <span className="text-sm font-medium text-slate-300">Place on Hold (Needs Clinical Decision)</span>
            </label>
            {onHold && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  On-hold reason <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={onHoldReason}
                  onChange={(e) => setOnHoldReason(e.target.value)}
                  placeholder="e.g. Waiting for specialist availability"
                  className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500/50"
                />
              </div>
            )}
          </div>

          {/* Override Decision */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
              Override Decision
              <span className="normal-case text-slate-500 ml-1 font-normal">
                (optional — Gate decision preserved in audit logs)
              </span>
            </label>
            <select
              value={overrideDecision}
              onChange={(e) => setOverrideDecision(e.target.value)}
              className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/50"
            >
              {OVERRIDE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {overrideDecision && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Override reason <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Clinical basis for override"
                    className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Override note <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    rows={3}
                    placeholder="Detailed clinical rationale for override"
                    className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Triage Note */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
              Triage Note
            </label>
            <textarea
              value={triageNote}
              onChange={(e) => setTriageNote(e.target.value)}
              rows={4}
              placeholder="Clinical notes for this triage review…"
              className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50 resize-none"
            />
          </div>

          {/* Override context — shown when a triage override exists */}
          {isOverridden(referral, workflow) && (() => {
            const od = getOverrideDetail(referral, workflow);
            return (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3">
                <p className="text-xs font-medium text-amber-400 uppercase tracking-wide mb-2">
                  Override Context
                </p>
                <div className="grid grid-cols-[5rem_1fr] gap-x-3 gap-y-1.5 text-xs items-baseline">
                  <span className="text-slate-500">Reason</span>
                  <span className="text-slate-300 break-words">{od.reason || '—'}</span>
                  <span className="text-slate-500">Note</span>
                  <span className="text-slate-300 break-words">{od.note || '—'}</span>
                  <span className="text-slate-500">By</span>
                  <MemberName uid={od.by} />
                  <span className="text-slate-500">At</span>
                  <span className="text-slate-300">{formatTs(od.at)}</span>
                </div>
              </div>
            );
          })()}

          </div>
          {/* ── end triage-drawer-actions wrapper ── */}

          {/* Referral Ping — triage_nurse can ask another user to act on this referral */}
          {workflow && canCreateReferralPing(role) && (
            <div
              className={`px-6 py-3${activeFocusTarget === 'triage_review_referral_ping' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`}
              data-learning-target="triage_review_referral_ping"
            >
              <PingComposer
                tenantId={tenantId}
                referralId={referralId}
                workflow={workflow}
                uid={uid}
                role={role}
                defaultPurpose={COMMUNICATION_PURPOSE.TRIAGE_CLARIFICATION}
              />
            </div>
          )}

        </div>

        {/* Error banner */}
        {saveError && (
          <div className="mx-6 mb-3 flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-300">{saveError}</p>
          </div>
        )}

        {/* Footer actions */}
        <div className={`px-6 py-4 border-t border-slate-800/50 flex flex-col gap-2 flex-shrink-0${activeFocusTarget === 'triage-drawer-footer' ? ' rounded-lg transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-drawer-footer">
          <div className="flex gap-3">
            <button
              onClick={() => handleSave(false)}
              disabled={saving || completing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm font-medium transition-colors"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4" /> Save</>
              )}
            </button>
            {!isCompleted && (
              <button
                onClick={() => handleSave(true)}
                disabled={saving || completing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                {completing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Completing…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4" /> Mark Complete</>
                )}
              </button>
            )}
          </div>
          {canReturn && (
            <button
              onClick={() => setShowReturnModal(true)}
              disabled={saving || completing || returning}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600/20 border border-amber-500/30 hover:bg-amber-600/30 hover:border-amber-500/50 disabled:opacity-50 text-amber-300 text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Return to Intake for Rework
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg text-slate-400 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>
        </div>

      </div>
    </>
  );
}

// ── Triage Queue Card ─────────────────────────────────────────────────────────

function TriageCard({ item, onClick }) {
  const { referral, workflow } = item;
  const outcome      = getReferralOutcome(referral);
  const triageStatus = deriveTriageStatus(workflow);
  const wfStatus     = workflow?.status;
  const priority     = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;

  return (
    <button
      onClick={() => onClick(item)}
      className="w-full text-left bg-slate-800/40 hover:bg-slate-800/70 border border-slate-700/50 hover:border-slate-600/70 rounded-xl p-4 transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {referral?.specialtyId?.replace(/_/g, ' ') ?? 'Unknown Specialty'}
          </p>
          <p className="text-xs text-slate-400 truncate mt-0.5">
            {referral?.packId?.replace(/_/g, ' ') ?? ''}
          </p>
          <p className="text-xs text-slate-500 font-mono mt-1.5">
            {referral?.id ?? referral?.referralId ?? '—'}
            <span className="text-slate-600 mx-1.5">·</span>
            {formatTs(workflow?.statusUpdatedAt ?? referral?.createdAt)}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300 flex-shrink-0 mt-1" />
      </div>

      {/* Badge row */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {wfStatus && (
          <RGStatusBadge type="workflow" status={wfStatus} />
        )}
        {outcome && (
          <RGStatusBadge type="outcome" status={outcome} />
        )}
        {isOverridden(referral, workflow) && (
          <span className={`${CHIP_CLS} bg-status-warning text-status-warning border-status-warning`}>Overridden</span>
        )}
        {priority !== WORKFLOW_PRIORITY.NORMAL && (
          <RGStatusBadge type="priority" status={priority} />
        )}
        <TriageStatusChip statusKey={triageStatus} />
      </div>
    </button>
  );
}

// ── Pending tab with group headers ────────────────────────────────────────────

function PendingTabContent({ items, onSelect }) {
  const groups = groupPendingItems(items);

  const sections = PENDING_GROUPS.map((cfg) => ({
    ...cfg,
    items: groups[cfg.key] ?? [],
  })).filter((s) => s.items.length > 0);

  if (sections.length === 0) {
    return (
      <EmptyState
        message="No referrals pending triage"
        detail="Referrals with Ready to Schedule status will appear here."
      />
    );
  }

  return (
    <div className="space-y-6">
      {sections.map(({ key, label, labelTw, dividerTw, items: groupItems }) => (
        <div key={key}>
          <div className={`flex items-center gap-3 mb-2`}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${labelTw}`}>
              {label}
            </p>
            <span className={`text-xs ${labelTw} opacity-60`}>
              {groupItems.length}
            </span>
            <div className={`flex-1 h-px border-t ${dividerTw}`} />
          </div>
          <div className="space-y-2">
            {groupItems.map((item) => (
              <TriageCard
                key={item.referral?.id ?? item.referral?.referralId}
                item={item}
                onClick={onSelect}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ message, detail }) {
  return (
    <div className="text-center py-16">
      <Filter className="w-10 h-10 text-slate-600 mx-auto mb-3" />
      <p className="text-slate-400 font-medium">{message}</p>
      <p className="text-slate-500 text-sm mt-1">{detail}</p>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function TriageQueueView({ initialTab, activeFocusTarget, onModalContext, autoOpenReferralId }) {
  const { user, tenant } = useAuth();
  const tenantId = user?.tenantId ?? tenant?.id ?? tenant?.tenantId;
  const uid      = user?.uid ?? user?.id;

  const PAGE_SIZE = 25;
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [activeTab,    setActiveTab]    = useState(initialTab || 'PENDING');
  const [selected,     setSelected]     = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [deepLinkNotice, setDeepLinkNotice] = useState('');

  // Reset page on tab switch.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeTab]);

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const raw = await listOpsActiveItems(tenantId);
      const eligible = raw.filter(({ workflow }) => {
        if (!workflow) return false;
        if (workflow.status === WORKFLOW_STATUS.READY_TO_SCHEDULE) return true;
        if (workflow.onHold === true)   return true;
        if (workflow.triage?.triageCompletedAt || workflow.triageCompletedAt) return true;
        return false;
      });
      setItems(eligible);
    } catch (err) {
      console.error('[TriageQueueView] load failed:', err);
      setLoadError(safeUserError(err) ?? 'Failed to load triage queue. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open the exact referral once when arriving from "Open Referral"
  // (Referral Pings). Fire-once guard prevents reopening after the user closes the
  // drawer; not-found shows a dismissible notice.
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets([{ tab: 'triage', items }], autoOpenReferralId);
    if (res.found) {
      setSelected(res.item);
      onModalContext?.('triage-drawer');
    } else {
      // Not triage-relevant / not loaded here — read-only fallback (non-PHI core)
      // for a specific reason instead of a vague "not available".
      getReferral(tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, items, onModalContext, tenantId]);

  // ── After save — patch item in-place ──────────────────────────────────────

  const handleSaved = useCallback((referralId, fields, markComplete) => {
    if (fields._returned) {
      setItems((prev) => prev.filter((item) =>
        (item.referral?.id ?? item.referral?.referralId) !== referralId
      ));
      setSelected(null);
      onModalContext?.(null);
      return;
    }
    setItems((prev) => prev.map((item) => {
      if ((item.referral?.id ?? item.referral?.referralId) !== referralId) return item;
      const updatedWorkflow = {
        ...item.workflow,
        ...fields,
        triage: {
          ...item.workflow?.triage,
          ...(markComplete ? {
            triageCompletedBy: uid,
            triageCompletedAt: { seconds: Date.now() / 1000 },
          } : {}),
        },
      };
      return { ...item, workflow: updatedWorkflow };
    }));
  }, [uid]);

  // ── Tab item lists ────────────────────────────────────────────────────────

  const tabCounts = {
    PENDING: items.filter((item) => deriveTriageStatus(item.workflow) === 'PENDING').length,
    ON_HOLD: items.filter((item) => deriveTriageStatus(item.workflow) === 'ON_HOLD').length,
    TRIAGED: items.filter((item) => deriveTriageStatus(item.workflow) === 'TRIAGED').length,
  };

  const currentTabItems = (() => {
    const filtered = items.filter((item) => deriveTriageStatus(item.workflow) === activeTab);
    if (activeTab === 'TRIAGED') {
      filtered.sort((a, b) => {
        const tsA = a.workflow?.triage?.triageCompletedAt || a.workflow?.triageCompletedAt;
        const tsB = b.workflow?.triage?.triageCompletedAt || b.workflow?.triageCompletedAt;
        const msA = tsA ? (tsA.toMillis ? tsA.toMillis() : (tsA.seconds || 0) * 1000) : 0;
        const msB = tsB ? (tsB.toMillis ? tsB.toMillis() : (tsB.seconds || 0) * 1000) : 0;
        return msB - msA;
      });
    }
    return filtered;
  })();

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading triage queue…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 max-w-lg mx-auto mt-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-300 font-medium mb-2">Failed to load triage queue</p>
          <p className="text-slate-400 text-sm mb-4">{loadError}</p>
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-medium mx-auto transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page header */}
      <div className="px-6 py-5 border-b border-slate-800/50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className={`text-lg font-semibold text-white${activeFocusTarget === 'triage-queue-title' ? ' rounded-lg px-2 -mx-2 transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-queue-title">Triage Queue</h1>
            <p className="text-xs text-slate-500">
              {tabCounts.PENDING + tabCounts.ON_HOLD} referral{(tabCounts.PENDING + tabCounts.ON_HOLD) !== 1 ? 's' : ''} eligible for triage
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {deepLinkNotice && (
        <div className="mx-6 mt-3 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className={`px-6 pt-4 flex gap-1 flex-wrap flex-shrink-0${activeFocusTarget === 'triage-queue-tabs' ? ' rounded-xl p-2 transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-queue-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${
              activeTab === id ? 'bg-status-needs-info text-status-needs-info' : 'bg-slate-700 text-slate-400'
            }`}>
              {tabCounts[id]}
            </span>
          </button>
        ))}
      </div>

      {/* Queue list */}
      <div className={`flex-1 overflow-y-auto px-6 py-4${activeFocusTarget === 'triage-queue-pending-list' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="triage-queue-pending-list">
        {activeTab === 'PENDING' ? (
          <>
            <PendingTabContent items={currentTabItems.slice(0, visibleCount)} onSelect={(item) => { setSelected(item); onModalContext?.('triage-drawer'); }} />
            {currentTabItems.length > visibleCount && (
              <div className="pt-3 pb-1 flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all"
                >
                  Load more
                </button>
                <p className="text-[11px] text-slate-600">{currentTabItems.length - visibleCount} remaining</p>
              </div>
            )}
          </>
        ) : currentTabItems.length === 0 ? (
          <EmptyState
            message={
              activeTab === 'ON_HOLD' ? 'No referrals on hold' :
              /* TRIAGED */             'No completed triage records'
            }
            detail={
              activeTab === 'ON_HOLD' ? 'Referrals placed on hold will appear here.' :
              /* TRIAGED */             'Completed triage reviews will appear here.'
            }
          />
        ) : (
          <>
            <div className="space-y-2">
              {currentTabItems.slice(0, visibleCount).map((item) => (
                <TriageCard
                  key={item.referral?.id ?? item.referral?.referralId}
                  item={item}
                  onClick={(item) => { setSelected(item); onModalContext?.('triage-drawer'); }}
                />
              ))}
            </div>
            {currentTabItems.length > visibleCount && (
              <div className="pt-3 pb-1 flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all"
                >
                  Load more
                </button>
                <p className="text-[11px] text-slate-600">{currentTabItems.length - visibleCount} remaining</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Triage Drawer */}
      {selected && (
        <TriageDrawer
          item={selected}
          uid={uid}
          role={user?.role}
          onClose={() => { setSelected(null); onModalContext?.(null); }}
          onSaved={handleSaved}
          activeFocusTarget={activeFocusTarget}
        />
      )}
    </div>
  );
}
